import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Column,
  Container,
  Copy,
  Flex,
  Heading,
  Image,
  Loader,
  Row,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { FormProvider, useForm } from 'react-hook-form/dist/index.ie11';
import {
  Breakpoint,
  useDeviceSize,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import classnames from 'classnames';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { Address } from '@grubhubprod/order-taking-client-sdk/src/sdk/types/geocode';
import {
  publish,
  subscribe,
} from '@grubhubprod/umami-library/dist/utilities/eventManager';
import { uniqBy } from '@grubhubprod/umami-library/dist/nodash/uniqBy';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { Input } from 'src/components/Input/Input';
import { getWindow } from 'src/utilities/window';
import { Select } from 'src/components/Select/Select';
import { STATES_LIST } from 'src/constants/states';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import { sdk } from 'src/utilities/sdk';
import { ISignupStepProps } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTrackerStep/SignupProgressTrackerStep';
import {
  getFormattedPhoneNumber,
  PhoneMaskInput,
} from 'src/components/PhoneMaskInput/PhoneMaskInput';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import {
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { AddressConfirmationModal } from 'src/components/AddressConfirmationModal/AddressConfirmationModal';
import { ICorp, ICorpLocation } from 'src/components/Account/Account';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { useGetQueryParams } from 'src/utilities/useGetQueryParams';
import { sanitizeAddressForGeocoding } from 'src/utilities/sanitizeAddressForGeocoding';
import { BusinessEmailControl } from 'src/components/selfService/features/Signup/steps/BusinessDetailsStep/BusinessEmailControl';
import { BusinessNameControl } from 'src/components/selfService/features/Signup/steps/BusinessDetailsStep/BusinessNameControl';
import { durationVital } from 'src/common/telemetry/DataDog/datadogRumApi';
import {
  BALANCE_ACCOUNT_INIT_DURATION_VITAL_NAME,
  BalanceSignupFlowType,
} from 'src/components/selfService/common/Balance/constants/balanceConstants';
import { DurationVitalStatus } from 'src/types/datadogRumApi';

type FormFields = {
  businessName: string;
  businessEmail: string;
  businessAddress: string;
  businessAddress2: string;
  city: string;
  state: string;
  zipcode: string;
  phoneNumber: string;
};

const emptyFormValues: FormFields = {
  businessName: '',
  businessEmail: '',
  businessAddress: '',
  businessAddress2: '',
  city: '',
  state: '',
  zipcode: '',
  phoneNumber: '',
};

const DESKTOP_MARGIN = 'md';

export function BusinessDetailsStep({
  updateMobileStepSize,
}: ISignupStepProps): JSX.Element {
  const { isStepCompleted, corpId, goToNextStep } = useSignupStatus();
  const [addressConfirmationModal, setAddressConfirmationModal] = useState({
    showModal: false,
    suggestedGeoAddresses: [],
    corpInfo: null,
  });
  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    sendTealiumPageView({
      pageGroup: 'corporate self service - sign up flow',
      pageName: 'business details',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = subscribe(
      'showAddressConfirmationModal',
      ({ corpInfo, suggestedGeoAddresses }) => {
        showAddressConfirmationModal(corpInfo, suggestedGeoAddresses);
      }
    );

    return () => {
      subscription();
    };
  }, []);

  function showAddressConfirmationModal(corpInfo, suggestedGeoAddresses) {
    setAddressConfirmationModal({
      showModal: true,
      suggestedGeoAddresses,
      corpInfo,
    });
  }

  function onAddressConfirmationSuccess(address: Address) {
    const {
      address_region: state,
      address_locality: city,
      address_sublocality: city2,
      street_address1: address1,
      street_address2: address2,
      postal_code: zip,
      latitude,
      longitude,
    } = address;

    const { location } = addressConfirmationModal.corpInfo;
    const corpInfo: ICorp = {
      ...addressConfirmationModal.corpInfo,
      location: {
        phone: location.phone,
        state,
        city: city || city2,
        address: address1,
        floor: address2 || location.floor,
        zip,
        latitude,
        longitude,
      },
    };
    setAddressConfirmationModal({
      showModal: false,
      corpInfo,
      suggestedGeoAddresses: null,
    });
  }

  function getAddressString({ address, city, state, zip }: ICorpLocation) {
    return `${address}, ${city}, ${state}, ${zip}`;
  }

  return (
    <Container variant="sm" className="businessDetailsStep_mainContainer">
      <BusinessDetailsForm
        corpInfo={addressConfirmationModal.corpInfo}
        updateMobileStepSize={updateMobileStepSize}
        isStepCompleted={isStepCompleted}
        corpId={corpId}
        goToNextStep={goToNextStep}
      />

      {addressConfirmationModal.showModal && (
        <AddressConfirmationModal
          currentAddressString={getAddressString(
            addressConfirmationModal.corpInfo.location
          )}
          suggestedGeoAddresses={addressConfirmationModal.suggestedGeoAddresses}
          onSuccess={onAddressConfirmationSuccess}
          onClose={() =>
            setAddressConfirmationModal({
              showModal: false,
              suggestedGeoAddresses: null,
              corpInfo: null,
            })
          }
        />
      )}
    </Container>
  );
}

interface IBusinessDetailsForm extends ISignupStepProps {
  isStepCompleted?: (SignupStep) => boolean;
  corpId: string;
  goToNextStep?: () => void;
  isAccount?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  corpInfo?: ICorp;
}

export function BusinessDetailsForm({
  updateMobileStepSize,
  isStepCompleted,
  corpId,
  goToNextStep,
  isAccount,
  onCancel,
  onSuccess,
  onError,
  corpInfo,
}: IBusinessDetailsForm): JSX.Element {
  const formMethods = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: emptyFormValues,
  });
  const {
    register,
    handleSubmit,
    errors,
    formState,
    watch,
    setValue,
    control,
  } = formMethods;
  const previousErrors = useRef({});
  const isUpdating = useGetQueryParams('isUpdating');

  const [isUpdatingStep] = useState(isUpdating);
  const { isValid } = formState;
  const { user } = useAuth();
  const window = getWindow();
  const { breakpoint } = useDeviceSize(window);
  const { status: submissionStatus, startAsyncTask } = useAsyncTask();
  const [states] = useState(STATES_LIST);
  const addressAutocompleteContainerRef = useRef<HTMLDivElement>(null);
  const [isAddressInputFocused, setIsAddressInputFocused] = useState(false);
  const [showAddressAutocomplete, setShowAddressAutocomplete] = useState(false);
  const [addressLatLong, setAddressLatLong] = useState({
    latitude: null,
    longitude: null,
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const debouncedBusinessAddress = useDebouncedValue(
    watch('businessAddress', '') as string,
    200
  );
  const hasExistingData = useRef(
    isAccount ? true : isStepCompleted(SignupStep.COMPANY)
  );
  const [isExistingDataLoaded, setIsExistingDataLoaded] = useState(false);
  const showLoadingState = hasExistingData.current && !isExistingDataLoaded;
  // Only name and business email are disabled for update flow, since those can not be modified in Balance API after creation
  const hasDisabledFields = !isAccount && hasExistingData.current;
  const formRef = useRef(null);

  useEffect(() => {
    async function getExistingCorpData() {
      try {
        let corpData: ICorp;
        if (corpInfo) {
          corpData = corpInfo;
        } else if (corpId) {
          const response = await sdk.http.get(
            `/corp-admin/corp/self/${corpId}`
          );
          corpData = get(response, 'object');
        }

        if (corpData) {
          const {
            name,
            business_email,
            location: {
              address,
              floor,
              phone,
              city,
              state,
              zip,
              latitude,
              longitude,
            },
          } = corpData;

          setValue('businessName', name, { shouldValidate: true });
          if (!isAccount) {
            setValue('businessEmail', business_email, {
              shouldValidate: true,
            });
          }
          setValue('businessAddress', address, { shouldValidate: true });
          setValue('businessAddress2', floor, { shouldValidate: true });
          setValue('phoneNumber', getFormattedPhoneNumber(phone), {
            shouldValidate: true,
          });
          setValue('city', city, { shouldValidate: true });
          setValue('state', state, { shouldValidate: true });
          setValue('zipcode', zip, { shouldValidate: true });

          // temporary until confirmation modal ticket
          setAddressLatLong({
            latitude,
            longitude,
          });

          setIsExistingDataLoaded(true);
        }
      } catch (error) {
        pushErrorFromAPI(error);
      }
    }

    if (hasExistingData.current || corpInfo) {
      getExistingCorpData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasExistingData, corpInfo, corpId]);

  // maxHeight of step on signup needs to be updated
  // when the form is rendered
  useEffect(() => {
    if (formRef.current && updateMobileStepSize) {
      updateMobileStepSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef]);

  // maxHeight of step on signup needs to be updated
  // when conditional form errors are rendered
  useEffect(() => {
    if (
      updateMobileStepSize &&
      errors &&
      previousErrors.current &&
      Object.keys(errors).length !== Object.keys(previousErrors.current).length
    ) {
      updateMobileStepSize();
      previousErrors.current = { ...errors };
    }
  });

  function onDocumentClick(event: Event): void {
    if (addressAutocompleteContainerRef.current) {
      const isClickWithinContainer =
        addressAutocompleteContainerRef.current.contains(event.target as Node);

      if (!isClickWithinContainer) {
        setIsAddressInputFocused(false);
      }
    }
  }

  useEffect(() => {
    if (window.document) {
      window.document.addEventListener('click', onDocumentClick);
    }

    return () => {
      window.document.removeEventListener('click', onDocumentClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const addressLength = debouncedBusinessAddress.length;

    setShowAddressAutocomplete(addressLength >= 3 && isAddressInputFocused);
  }, [debouncedBusinessAddress, isAddressInputFocused]);

  useEffect(() => {
    async function getAddressSuggestions() {
      try {
        const results = await sdk.rest.geocode.autocomplete(
          encodeURIComponent(debouncedBusinessAddress)
        );

        setAddressSuggestions(results);
      } catch (e) {
        pushErrorFromAPI(e, {
          fallbackMessage:
            'Sorry, we were unable to verify your address, please try again',
        });
      }
    }
    if (showAddressAutocomplete && debouncedBusinessAddress) {
      getAddressSuggestions();
    }
  }, [showAddressAutocomplete, debouncedBusinessAddress]);

  useEffect(() => {
    if (submissionStatus === AsyncTaskStatus.COMPLETE) {
      if (isAccount) {
        onSuccess();
      } else {
        goToNextStep();
      }
    }

    if (submissionStatus === AsyncTaskStatus.ERROR && isAccount) {
      onError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionStatus]);

  const isPhone = breakpoint < Breakpoint.sm;

  async function onFormSubmit({
    businessName,
    businessEmail,
    businessAddress,
    businessAddress2,
    city,
    state,
    phoneNumber,
    zipcode,
  }: FormFields) {
    const { latitude, longitude } = addressLatLong;

    const addressString = `${businessAddress}, ${city}, ${state}, ${zipcode}`;
    const sanitizedAddress = sanitizeAddressForGeocoding(addressString);
    const suggestedGeoAddresses: Address[] =
      await sdk.rest.geocode.selectAddress(
        encodeURIComponent(sanitizedAddress)
      );

    // WDA-26352 do not show up the selection modal for same repeated address
    const uniqueSuggestedGeoAddresses = uniqBy(
      suggestedGeoAddresses,
      (a) =>
        `${a.street_address1}, ${a.address_locality}, ${a.address_region}, ${a.postal_code}`
    );

    const corpInfo: ICorp = {
      diner_id: user.ud_id,
      name: businessName,
      ...(!isAccount && { business_email: businessEmail }),
      location: {
        address: businessAddress,
        floor: businessAddress2,
        city,
        state,
        phone: phoneNumber,
        zip: zipcode,
        latitude,
        longitude,
      },
    };

    const queryParamsString = window.location.search;

    if (queryParamsString) {
      corpInfo.utm_details = queryParamsString;
    }

    if (uniqueSuggestedGeoAddresses.length > 1) {
      publish('showAddressConfirmationModal', {
        corpInfo,
        suggestedGeoAddresses: uniqueSuggestedGeoAddresses,
      });
      return;
    }

    const [suggestedAddress] = uniqueSuggestedGeoAddresses;

    // update lat/long, address, state, city and zip code from geo suggestion in case autocomplete was not used
    corpInfo.location = {
      ...corpInfo.location,
      address: suggestedAddress.street_address1 || businessAddress, // fallback to user input value in case no geo result, in order to avoid a location.required error
      city: suggestedAddress.address_locality || city,
      state: suggestedAddress.address_region || state,
      zip: suggestedAddress.postal_code || zipcode,
      latitude: suggestedAddress.latitude,
      longitude: suggestedAddress.longitude,
    };

    try {
      durationVital(BALANCE_ACCOUNT_INIT_DURATION_VITAL_NAME)?.start({
        description:
          'Measures the delay from the moment a user saves the business details to when the validation step is displayed.',
        context: {
          flow_type: hasExistingData.current
            ? BalanceSignupFlowType.MODIFICATION
            : BalanceSignupFlowType.CREATION,
        },
      });

      if (hasExistingData.current) {
        await startAsyncTask(() => {
          return sdk.http.put(`/corp-admin/corp/self/${corpId}`, corpInfo);
        });
      } else {
        await startAsyncTask(() => {
          return sdk.http.post(`/corp-admin/corp/self`, corpInfo);
        });
      }
    } catch (error) {
      durationVital(BALANCE_ACCOUNT_INIT_DURATION_VITAL_NAME)?.stop({
        options: {
          context: { status: DurationVitalStatus.FAILURE },
        },
      });

      pushErrorFromAPI(error);
    }
  }

  function getButtonCopy(): JSX.Element {
    if (isAccount || isUpdatingStep) {
      return <span>Update</span>;
    }

    return <span>{isPhone ? 'Next' : 'Save and continue'}</span>;
  }

  async function getFullAddress(address: string) {
    const [fullAddress] = await sdk.rest.geocode.selectAddress(address);

    if (fullAddress) {
      const {
        address_region: state,
        address_locality: city,
        address_sublocality: city2,
        street_address1: address1,
        street_address2: address2,
        postal_code: zipcode,
        latitude,
        longitude,
      } = fullAddress;

      setValue('businessAddress', address1, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('businessAddress2', address2, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('city', city || city2, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('state', state, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('zipcode', zipcode, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddressLatLong({
        latitude,
        longitude,
      });
    }

    setIsAddressInputFocused(false);
  }

  // we want to keep the submission button disabled when the submission
  // is complete so no extra clicks happen before the next step renders
  const shouldDisableSubmissionButton =
    !isValid ||
    submissionStatus === AsyncTaskStatus.IN_PROGRESS ||
    submissionStatus === AsyncTaskStatus.COMPLETE;

  return (
    <FormProvider {...formMethods}>
      {showLoadingState && (
        <Flex
          alignItems="center"
          justifyContent="center"
          backgroundColor="globals.white75a"
          position="absolute"
          width="100%"
          height="100%"
          zIndex={10}
        >
          <Loader size="md" />
        </Flex>
      )}
      {!isAccount && (
        <>
          <Stack y="xxx-sm">
            <Heading variant="h2">Set up your business profile</Heading>
          </Stack>
          <Stack y="md">
            <Copy variant="body">
              To get started, we need to know a few details. Make sure
              everything is correct—you won’t have an opportunity to update it
              until your account is approved.
            </Copy>
          </Stack>
        </>
      )}
      <Square
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(isAccount && { inset: 'md' })}
      >
        <form ref={formRef}>
          <Stack y={DESKTOP_MARGIN}>
            <Row gutter={0}>
              <Column gutter={0}>
                <Stack y={!isAccount ? 'xxx-sm' : 'none'}>
                  <BusinessNameControl
                    name="businessName"
                    id="businessName"
                    disabled={hasDisabledFields}
                  />
                </Stack>

                {!isAccount && (
                  <Copy variant="caption" color="secondaryText">
                    Business name cannot be changed
                  </Copy>
                )}
              </Column>
            </Row>
          </Stack>

          {!isAccount && (
            <Stack y={DESKTOP_MARGIN}>
              <Row gutter={0}>
                <Column gutter={0}>
                  <Stack y="xxx-sm">
                    <BusinessEmailControl
                      name="businessEmail"
                      id="businessEmail"
                      rules={{ required: 'Business email is required' }}
                      disabled={hasDisabledFields}
                    />
                  </Stack>
                  <Copy variant="caption" color="secondaryText">
                    Business email cannot be changed
                  </Copy>
                </Column>
              </Row>
            </Stack>
          )}

          <Row gutter={12}>
            <Column gutter={12} tablet={6}>
              <Stack y={DESKTOP_MARGIN}>
                <div
                  className="businessDetailsStep_businessAddressContainer"
                  ref={addressAutocompleteContainerRef}
                >
                  <Input
                    data-testid="business-address-input"
                    label="Business address"
                    name="businessAddress"
                    id="businessAddress"
                    ref={register({
                      required: 'Business address is required',
                      minLength: {
                        value: 3,
                        message: 'Please enter a valid address',
                      },
                      maxLength: {
                        value: 255,
                        message:
                          'Business address must be 255 characters or less',
                      },
                    })}
                    error={errors.businessAddress}
                    onFocus={() => {
                      setIsAddressInputFocused(true);
                    }}
                  />
                  {showAddressAutocomplete && (
                    <Flex
                      bg="globals.white"
                      flexDirection="column"
                      border="1px solid"
                      borderColor="darkLine"
                      borderRadius="0 0 4px 4px"
                      data-testid="address-suggestions-container"
                      className="businessDetailsStep_addressSuggestionsContainer"
                    >
                      <ul className="businessDetailsStep_addressSuggestions">
                        {addressSuggestions.map((address) => (
                          <li
                            key={address}
                            className="businessDetailsStep_addressSuggestion"
                            data-testid="address-suggestions-item"
                          >
                            <Button
                              variant="tertiary"
                              type="button"
                              className="businessDetailsStep_addressSuggestionBtn"
                              onClick={() => getFullAddress(address)}
                            >
                              <Copy variant="body" color="secondaryText">
                                {address}
                              </Copy>
                            </Button>
                          </li>
                        ))}
                      </ul>
                      <Flex justifyContent="center">
                        <Square inset="x-sm">
                          <Image
                            src="https://media-cdn.grubhub.com/image/upload/v1612897160/corporate/powered-by-google.svg"
                            alt="Powered by Google"
                          />
                        </Square>
                      </Flex>
                    </Flex>
                  )}
                </div>
              </Stack>
            </Column>
            <Column gutter={12} tablet={6}>
              <Stack y={DESKTOP_MARGIN}>
                <Input
                  label="Suite, floor (optional)"
                  name="businessAddress2"
                  id="businessAddress2"
                  ref={register({
                    maxLength: {
                      value: 255,
                      message: 'Suite, floor must be 255 characters or less',
                    },
                  })}
                  error={errors.businessAddress2}
                />
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            <Column gutter={12} tablet={6}>
              <Stack y={DESKTOP_MARGIN}>
                <Input
                  label="City"
                  name="city"
                  id="city"
                  error={errors.city}
                  ref={register({
                    required: 'City is required',
                    maxLength: {
                      value: 60,
                      message: 'City must be 60 characters or less',
                    },
                  })}
                />
              </Stack>
            </Column>
            <Column gutter={12} tablet={6}>
              <Stack y={DESKTOP_MARGIN}>
                <Select
                  label="State"
                  name="state"
                  id="state"
                  error={errors.state}
                  ref={register({ required: 'State is required' })}
                >
                  <option value={null}> </option>
                  {states.map((state) => (
                    <option key={state.abbreviation} value={state.abbreviation}>
                      {state.name}
                    </option>
                  ))}
                </Select>
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            <Column gutter={12} tablet={6}>
              <Stack y={DESKTOP_MARGIN}>
                <Input
                  label="Zip code"
                  name="zipcode"
                  id="zipcode"
                  type="tel"
                  error={errors.zipcode}
                  ref={register({
                    required: 'Zip code is required',
                    pattern: {
                      value: /^[0-9]{5}$/,
                      message: 'Zip code should be 5 numbers',
                    },
                  })}
                />
              </Stack>
            </Column>
            <Column gutter={12} tablet={6}>
              <Stack y={isPhone ? 'none' : DESKTOP_MARGIN}>
                <PhoneMaskInput
                  label="Your phone number"
                  id="phoneNumber"
                  name="phoneNumber"
                  required
                  control={control}
                  error={errors.phoneNumber}
                />
              </Stack>
            </Column>
          </Row>
        </form>
      </Square>

      {isAccount ? (
        <Square as="footer" dimension="2" inset="md">
          <Flex
            flexWrap={isPhone ? 'wrap' : 'nowrap'}
            flexDirection={isPhone ? 'column-reverse' : 'row'}
          >
            <Stack
              width={isPhone ? '100%' : '50%'}
              x={isPhone ? 'none' : 'x-sm'}
            >
              <Button
                block
                variant="secondary"
                buttonSize="medium"
                onClick={onCancel}
              >
                Cancel
              </Button>
            </Stack>
            <Stack y={isPhone ? 'sm' : 'none'} width={isPhone ? '100%' : '50%'}>
              <Button
                block
                variant="primary"
                buttonSize="medium"
                disabled={shouldDisableSubmissionButton}
                isLoading={submissionStatus === AsyncTaskStatus.IN_PROGRESS}
                onClick={handleSubmit(onFormSubmit)}
              >
                {getButtonCopy()}
              </Button>
            </Stack>
          </Flex>
        </Square>
      ) : (
        <SignupStepFooter>
          <Flex justifyContent="flex-end">
            <Button
              className={classnames('', {
                businessDetailsStep_primaryFooterButton: !isPhone,
              })}
              buttonSize="medium"
              type="submit"
              disabled={shouldDisableSubmissionButton}
              isLoading={submissionStatus === AsyncTaskStatus.IN_PROGRESS}
              block={isPhone}
              onClick={handleSubmit(onFormSubmit)}
            >
              {getButtonCopy()}
            </Button>
          </Flex>
        </SignupStepFooter>
      )}
    </FormProvider>
  );
}
